import React, { useState, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import DatePicker from "react-datepicker";
import moment from "moment";
import { styles } from "../../../utils/helper";
import axios from "axios";
import { API_URL } from "../../../config";
import { toast } from "react-toastify";
const JobDetailsForm = ({ formik }) => {
  const [jobRoleOptions, setJobRoleOptions] = useState([]);
  const [isSearchingJobRoles, setIsSearchingJobRoles] = useState(false);
  const [cityOptions, setCityOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [customCities, setCustomCities] = useState([]);

  // Check if job type is internship
  const isInternship = formik.values.jobType === "internship";

  // Helper function to show field error
  const showFieldError = (fieldName) =>
    formik.touched[fieldName] && formik.errors[fieldName];

  const jobTypeOptions = [
    { value: "full-time", label: "Full Time" },
    { value: "part-time", label: "Part Time" },
    { value: "contract", label: "Contract" },
    { value: "internship", label: "Internship" },
    { value: "remote", label: "Work From Home" },
  ];

  const searchJobRoles = async (search) => {
    if (!search || search.length < 2) {
      setJobRoleOptions([]);
      return;
    }

    setIsSearchingJobRoles(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/freshment_employer_portal/CommonAPI/search_job_roles_api?search=${search}`
      );

      if (response.data.success) {
        const formattedJobRoles = response.data.data.map((role) => ({
          value: role.id,
          label: role.job_role_name,
        }));
        setJobRoleOptions(formattedJobRoles);
      } else {
        console.error("Failed to fetch job roles:", response.data.message);
      }
    } catch (error) {
      console.error("Error searching job roles:", error);
    } finally {
      setIsSearchingJobRoles(false);
    }
  };

  const fetchCities = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/freshment_employer_portal/CompanyDashboard/get_job_locations`
      );

      if (response.data.success) {
        const formattedCities = response.data.data.map((city) => {
          // Make sure we have city_name properly set
          const cityOption = {
            value: city.id,
            label: city.city,
            city_name: city.city_name || city.city, // Fallback to city if city_name is not available
          };
          return cityOption;
        });
        setCityOptions(formattedCities);
      } else {
        console.error("Failed to fetch cities:", response.data.message);
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
      toast.error("Failed to load city data. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchCities();
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, []);

  // Set up the job location from initial values once cities are loaded
  useEffect(() => {
    if (cityOptions.length > 0) {
      if (formik.values.jobLocationId) {
        // Try to find the city by ID first
        const matchedCity = cityOptions.find(
          (city) => city.value === formik.values.jobLocationId
        );

        if (matchedCity) {
          // If we found a matching city in our options, use it
          const cityName = matchedCity.city_name || matchedCity.label;
          formik.setFieldValue("jobLocation", cityName);
        } else if (formik.values.jobLocation) {
          // If no match but we have a job location name, create a custom city
          const customCity = {
            value: formik.values.jobLocationId,
            label: formik.values.jobLocation,
            city_name: formik.values.jobLocation,
          };
          setCustomCities((prev) => [...prev, customCity]);
        }
      } else if (formik.values.jobLocation) {
        // If we only have the job location name but no ID
        // Look for a city with matching name
        const matchedCity = cityOptions.find(
          (city) =>
            (city.city_name &&
              city.city_name.toLowerCase() ===
              formik.values.jobLocation.toLowerCase()) ||
            (city.label &&
              city.label.toLowerCase() ===
              formik.values.jobLocation.toLowerCase())
        );

        if (matchedCity) {
          console.log("Found matched city by name:", matchedCity);
          formik.setFieldValue("jobLocationId", matchedCity.value);
        } else {
          // Create a custom city with a unique ID
          const customCity = {
            value: `custom_${formik.values.jobLocation
              .toLowerCase()
              .replace(/\s+/g, "_")}`,
            label: formik.values.jobLocation,
            city_name: formik.values.jobLocation,
          };
          console.log("Creating new custom city:", customCity);
          setCustomCities((prev) => [...prev, customCity]);
          formik.setFieldValue("jobLocationId", customCity.value);
        }
      }
    }
  }, [cityOptions, formik.values.jobLocationId, formik.values.jobLocation]);

  return (
    <>
      <h5 className="mb-4">Job Details</h5>
      <Row className="g-3">
        <Col md={6}>
          <div className="form-group mb-3">
            <label htmlFor="companyName" className="form-label fw-semibold">
              Company Name <span className="text-danger">*</span>
            </label>
            <input
              value={formik.values.companyName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              type="text"
              name="companyName"
              className={`form-control ${showFieldError("companyName") ? "is-invalid" : ""
                }`}
              placeholder="Company Name"
            />
            {showFieldError("companyName") && (
              <div className="text-danger mt-1">
                {formik.errors.companyName}
              </div>
            )}
          </div>
        </Col>

        <Col md={6}>
          <div className="form-group mb-3">
            <label htmlFor="jobPostName" className="form-label fw-semibold">
              Job Role <span className="text-danger">*</span>
            </label>
            <CreatableSelect
              id="jobPostName"
              name="jobPostName"
              options={jobRoleOptions}
              styles={styles}
              onChange={(option) => {
                if (option) {
                  formik.setFieldValue("jobPostName", option);
                } else {
                  formik.setFieldValue("jobPostName", "");
                }
              }}
              value={
                jobRoleOptions.find(
                  (option) => option.value === formik.values.jobPostName?.value
                ) || formik.values.jobPostName
              }
              noOptionsMessage={({ inputValue }) =>
                inputValue.length < 2
                  ? "Type at least 2 characters to search"
                  : "No job roles found"
              }
              placeholder="Select Job Role"
              isLoading={isSearchingJobRoles}
              onInputChange={(input) => {
                searchJobRoles(input);
              }}
              formatCreateLabel={(input) =>
                `Create "${input}" as a new job role`
              }
              className={showFieldError("jobPostName") ? "is-invalid" : ""}
              isClearable
            />
            {showFieldError("jobPostName") && (
              <div className="text-danger mt-1">
                {formik.errors.jobPostName}
              </div>
            )}
          </div>
        </Col>

        <Col md={6}>
          <div className="form-group mb-3">
            <label htmlFor="jobType" className="form-label fw-semibold">
              Job Type <span className="text-danger">*</span>
            </label>
            <Select
              id="jobType"
              name="jobType"
              options={jobTypeOptions}
              styles={styles}
              onChange={(option) => {
                formik.setFieldValue("jobType", option.value);
              }}
              value={jobTypeOptions.find(
                (option) => option.value === formik.values.jobType
              )}
              placeholder="Select Job Type"
              className={showFieldError("jobType") ? "is-invalid" : ""}
            />
            {showFieldError("jobType") && (
              <div className="text-danger mt-1">{formik.errors.jobType}</div>
            )}
          </div>
        </Col>

        <Col md={6}>
          <div className="form-group mb-3">
            <label htmlFor="jobLocation" className="form-label fw-semibold">
              Job Location <span className="text-danger">*</span>
            </label>
            <CreatableSelect
              styles={styles}
              name="jobLocation"
              options={[...cityOptions, ...customCities]}
              value={
                formik.values.jobLocationId
                  ? cityOptions.find(
                    (option) => option.value === formik.values.jobLocationId
                  ) ||
                  customCities.find(
                    (option) => option.value === formik.values.jobLocationId
                  ) ||
                  (formik.values.jobLocation
                    ? {
                      value: formik.values.jobLocationId,
                      label: formik.values.jobLocation,
                      city_name: formik.values.jobLocation,
                    }
                    : null)
                  : null
              }
              onChange={(option) => {
                formik.setFieldValue(
                  "jobLocationId",
                  option ? option.value : ""
                );
                // Make sure we're using city_name if available, otherwise fall back to label
                formik.setFieldValue(
                  "jobLocation",
                  option ? option.city_name || option.label : ""
                );
                // For debugging
                console.log("Selected location:", option);
              }}
              onCreateOption={(inputValue) => {
                const newOption = {
                  value: `custom_${inputValue
                    .toLowerCase()
                    .replace(/\s+/g, "_")}`,
                  label: inputValue,
                  city_name: inputValue, // Ensure city_name is set properly
                };
                setCustomCities((prev) => [...prev, newOption]);
                formik.setFieldValue("jobLocationId", newOption.value);
                formik.setFieldValue("jobLocation", inputValue); // Directly use inputValue
                console.log("Created new location:", newOption);
              }}
              placeholder="Select or type a city"
              isLoading={isLoading}
              loadingMessage={() => (
                <div className="d-flex align-items-center">
                  <div
                    className="spinner-border spinner-border-sm text-primary me-2"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Loading cities...
                </div>
              )}
              formatCreateLabel={(inputValue) =>
                `Add "${inputValue}" as a new job location`
              }
              className={showFieldError("jobLocation") ? "is-invalid" : ""}
              isClearable
            />
            {showFieldError("jobLocation") && (
              <div className="text-danger">{formik.errors.jobLocation}</div>
            )}
          </div>
        </Col>

        <Col md={6}>
          <div className="form-group mb-3">
            <label htmlFor="salaryStipend" className="form-label fw-semibold">
              {isInternship ? "Stipend" : "Salary"}{" "}
              <span className="text-danger">*</span>
              {isInternship ? (
                <small className="text-muted"> (15000 / Per Month)</small>
              ) : (
                <small className="text-muted">
                  {" "}
                  (Annual CTC or Salary (500000) ~ 5 LPA)
                </small>
              )}
            </label>
            <input
              value={formik.values.salaryStipend}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              type="number"
              name="salaryStipend"
              className={`form-control ${showFieldError("salaryStipend") ? "is-invalid" : ""
                }`}
              placeholder={isInternship ? "15000" : "500000"}
            />
            {showFieldError("salaryStipend") && (
              <div className="text-danger mt-1">
                {formik.errors.salaryStipend}
              </div>
            )}
          </div>
        </Col>

        <Col md={6}>
          <div className="form-group mb-3">
            <label
              htmlFor="compensationDetails"
              className="form-label fw-semibold"
            >
              Compensation Details{" "}
              <small className="text-muted">
                {" "}
                (5 LPA CTC + 2 LPA On Performance basis)
              </small>
            </label>
            <input
              type="text"
              name="compensationDetails"
              className={`form-control ${showFieldError("compensationDetails") ? "is-invalid" : ""
                }`}
              placeholder="Enter compensation details"
              value={formik.values.compensationDetails}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {showFieldError("compensationDetails") && (
              <div className="text-danger mt-1">
                {formik.errors.compensationDetails}
              </div>
            )}
          </div>
        </Col>

        {isInternship && (
          <>
            <Col md={12}>
              <div className="form-group mb-3">
                <div className="form-check">
                  <input
                    type="checkbox"
                    id="prePlacementOffer"
                    name="prePlacementOffer"
                    className="form-check-input"
                    checked={formik.values.prePlacementOffer}
                    onChange={(e) => {
                      formik.handleChange(e);
                    }}
                  />
                  <label
                    className="form-check-label fw-semibold"
                    htmlFor="prePlacementOffer"
                  >
                    Would you like to offer a Pre-Placement Offer?
                  </label>
                </div>
              </div>
            </Col>

            {formik.values.prePlacementOffer && (
              <Col md={12}>
                <div className="form-group mb-3">
                  <label
                    htmlFor="prePlacementOfferEligibilityCriteria"
                    className="form-label fw-semibold"
                  >
                    Pre-Placement Offer Eligibility Criteria
                  </label>
                  <textarea
                    name="prePlacementOfferEligibilityCriteria"
                    className={`form-control ${showFieldError("prePlacementOfferEligibilityCriteria")
                        ? "is-invalid"
                        : ""
                      }`}
                    placeholder="Enter eligibility criteria for pre-placement offer"
                    value={formik.values.prePlacementOfferEligibilityCriteria}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    rows={3}
                    style={{ height: "100px" }}
                  />
                  {showFieldError("prePlacementOfferEligibilityCriteria") && (
                    <div className="text-danger mt-1">
                      {formik.errors.prePlacementOfferEligibilityCriteria}
                    </div>
                  )}
                </div>
              </Col>
            )}
          </>
        )}

        <Col md={6}>
          <div className="form-group mb-3">
            <label htmlFor="totalVacancy" className="form-label fw-semibold">
              Total Vacancy <span className="text-danger">*</span>
            </label>
            <input
              value={formik.values.totalVacancy}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              type="number"
              name="totalVacancy"
              className={`form-control ${showFieldError("totalVacancy") ? "is-invalid" : ""
                }`}
              placeholder="Total Vacancy"
            />
            {showFieldError("totalVacancy") && (
              <div className="text-danger mt-1">
                {formik.errors.totalVacancy}
              </div>
            )}
          </div>
        </Col>
        <Col md={6}>
          <div className="form-group mb-3">
            <label htmlFor="joiningDate" className="form-label fw-semibold">
              Joining Date <span className="text-danger">*</span>
            </label>
            <div>
              <DatePicker
                selected={
                  formik.values.joiningDate
                    ? moment(formik.values.joiningDate).toDate()
                    : null
                }
                onChange={(date) => {
                  formik.setFieldValue("joiningDate", date);
                }}
                dateFormat="yyyy-MM-dd"
                minDate={moment().toDate()}
                className={`form-control ${showFieldError("joiningDate") ? "is-invalid" : ""
                  }`}
                placeholderText="Select joining date"
                name="joiningDate"
                autoComplete="off"
              />
            </div>
            {showFieldError("joiningDate") && (
              <div className="text-danger mt-1">
                {formik.errors.joiningDate}
              </div>
            )}
          </div>
        </Col>

        <Col md={12}>
          <div className="form-group mb-3">
            <label htmlFor="companyAddress" className="form-label fw-semibold">
              Company Address <span className="text-danger">*</span>
            </label>
            <input
              value={formik.values.companyAddress}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="companyAddress"
              className={`form-control ${showFieldError("companyAddress") ? "is-invalid" : ""
                }`}
              placeholder="Enter company address"
            />
            {showFieldError("companyAddress") && (
              <div className="text-danger mt-1">
                {formik.errors.companyAddress}
              </div>
            )}
          </div>
        </Col>

        <Col md={6}>
          <div className="form-group mb-3">
            <label htmlFor="companyHRName" className="form-label fw-semibold">
              HR Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              name="companyHRName"
              className={`form-control ${showFieldError("companyHRName") ? "is-invalid" : ""
                }`}
              placeholder="Enter HR Name"
              value={formik.values.companyHRName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {showFieldError("companyHRName") && (
              <div className="text-danger mt-1">
                {formik.errors.companyHRName}
              </div>
            )}
          </div>
        </Col>
        <Col md={6}>
          <div className="form-group mb-3">
            <label htmlFor="companyHREmail" className="form-label fw-semibold">
              HR Email <span className="text-danger">*</span>
            </label>
            <input
              type="email"
              name="companyHREmail"
              className={`form-control ${showFieldError("companyHREmail") ? "is-invalid" : ""
                }`}
              placeholder="john@gmail.com"
              value={formik.values.companyHREmail}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {showFieldError("companyHREmail") && (
              <div className="text-danger mt-1">
                {formik.errors.companyHREmail}
              </div>
            )}
          </div>
        </Col>

        <Col md={12}>
          <div className="form-group mb-3">
            <label htmlFor="applyOnlineURL" className="form-label fw-semibold">
              External Apply URL (Optional)
            </label>
            <input
              type="url"
              name="applyOnlineURL"
              className={`form-control ${showFieldError("applyOnlineURL") ? "is-invalid" : ""
                }`}
              placeholder="https://www.domain.com"
              value={formik.values.applyOnlineURL}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {showFieldError("applyOnlineURL") && (
              <div className="text-danger mt-1">
                {formik.errors.applyOnlineURL}
              </div>
            )}
          </div>
        </Col>

        <Col md={6}>
          <div className="form-group mb-3">
            <label
              htmlFor="companyHRContact"
              className="form-label fw-semibold"
            >
              HR Contact Number <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              name="companyHRContact"
              className={`form-control ${showFieldError("companyHRContact") ? "is-invalid" : ""
                }`}
              placeholder="0123456789"
              value={formik.values.companyHRContact}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              maxLength={15}
              minLength={10}
              onInput={(e) => {
                e.target.value = e.target.value.replace(/[^0-9]/g, "");
              }}
            />
            {showFieldError("companyHRContact") && (
              <div className="text-danger mt-1">
                {formik.errors.companyHRContact}
              </div>
            )}
          </div>
        </Col>

        <Col md={6}>
          <div className="form-group mb-3">
            <label
              htmlFor="alternateContactDetails"
              className="form-label fw-semibold"
            >
              Alternate Contact Details
            </label>
            <input
              type="text"
              name="alternateContactDetails"
              className={`form-control ${showFieldError("alternateContactDetails") ? "is-invalid" : ""
                }`}
              placeholder="Enter alternate contact details"
              value={formik.values.alternateContactDetails}
              onChange={formik.handleChange}
              minLength={10}
              maxLength={12}
              onInput={(e) => {
                e.target.value = e.target.value.replace(/[^0-9]/g, "");
              }}
              onBlur={formik.handleBlur}
            />
            {showFieldError("alternateContactDetails") && (
              <div className="text-danger mt-1">
                {formik.errors.alternateContactDetails}
              </div>
            )}
          </div>
        </Col>
      </Row>
    </>
  );
};

export default JobDetailsForm;
